import BigNumber from 'bignumber.js'
import { startOfWeek } from 'date-fns'
import { getIconUrl } from '../../services/static_data'
import { findLast } from '../../utils'
import { PoolMetadata, PoolRawData, PoolsMetadataResponse, TotalVolume, TvlResponse, VolumeResponse } from './chartsApi'
import { ProcessedData, ProcessedPoolData, ProcessedTokenData, ProcessedTvlData, ProcessedVolumeData } from './types'

export const processData = ({ poolsMetadata, poolsRawData }: { poolsMetadata: PoolsMetadataResponse; poolsRawData: Array<PoolRawData> }): ProcessedData => {
    const tokens: Tokens = {}
    const poolsData: ProcessedPoolData[] = []
    const token_price_by_hour: TokenPriceByHour = {}
    const total_tvl_by_hour: TotalTvlByHour = {}
    const total_volume_by_day: TotalVolumeByDay = {}
    const apr: Record<string, { apr: number; apr_cl: number; ratio: number; percentage: number }> = {}

    // for each pool
    poolsRawData.forEach((poolRawData) => {
        const metadata = poolsMetadata.pools.find((p) => p.poolkey === poolRawData.poolId)
        if (!metadata) throw Error(`invalid data. poolId=${poolRawData.poolId}`)

        apr[poolRawData.poolId] = {
            percentage: poolRawData.apr.data[0].percentage,
            apr: poolRawData.apr.data[0].apr,
            apr_cl: poolRawData.apr.data[0].apr_cl,
            ratio: poolRawData.apr.data[0].ratio
        }
        // const tokenSymbols = [metadata.token0.symbol, metadata.token1.symbol]
        const tokenSymbolsWithAddress = [
            { symbol: metadata.token0.symbol, address: metadata.token0.address },
            { symbol: metadata.token1.symbol, address: metadata.token1.address }
        ]
        tokenSymbolsWithAddress.forEach((token) => {
            if (!tokens[token.symbol]) {
                tokens[token.symbol] = {
                    address: token.address!,
                    symbol: token.symbol,
                    name: TOKEN_SYMBOL_TO_NAME[token.symbol],
                    icon: getIconUrl('mainnet-alpha', token.address)!,
                    price: BigNumber(0),
                    priceChange: BigNumber(0),
                    tvl: BigNumber(0),
                    volume_24h: BigNumber(0)
                }
            }
        })

        poolRawData.tvl.data.sort((a, b) => a.timestamp - b.timestamp)
        poolRawData.volume.data.sort((a, b) => a.timestamp - b.timestamp)

        const { pool_tvl, pool_24_tvl } = processPoolTvlData({
            tvlResponse: poolRawData.tvl,
            poolMetadata: metadata,
            total_tvl_by_hour,
            token_price_by_hour,
            tokens
        })

        const { pool_volume_24h, pool_volume_7d, pool_volume_prev_24h } = processPoolVolumeData({
            volumeResponse: poolRawData.volume,
            total_volume_by_day,
            tokens
        })

        const pooldData: ProcessedPoolData = {
            pool_id: poolRawData.poolId,
            pool_fee: metadata.pool_fee,
            token1_address: metadata.token0.address,
            token2_address: metadata.token1.address,
            token1_symbol: metadata.token0.symbol,
            token2_symbol: metadata.token1.symbol,
            tvl: pool_tvl,
            tvl_24h: pool_24_tvl,
            volume_24h: pool_volume_24h,
            volume: poolRawData.volume,
            volume_7d: pool_volume_7d,
            pool_volume_prev_24h: pool_volume_prev_24h,
            apr: poolRawData.apr.data[0]
        }

        poolsData.push(pooldData)
    })

    const tvl_data = calcTvlSummary(total_tvl_by_hour)
    const volume_data = calcVolumeSummary(total_volume_by_day)

    setTokensCurrentPrice(tokens, token_price_by_hour)

    return {
        apr: apr,
        TVL: tvl_data,
        volume: volume_data,
        pools: poolsData,
        tokens: Object.values(tokens)
    }
}

function processPoolVolumeData({ volumeResponse, total_volume_by_day, tokens }: { volumeResponse: VolumeResponse; total_volume_by_day: TotalVolumeByDay; tokens: Tokens }): {
    pool_volume_24h: BigNumber
    pool_volume_7d: BigNumber
    pool_volume_prev_24h: BigNumber
} {
    let pool_volume_7d = BigNumber(0)
    let pool_volume_24h = BigNumber(0)
    let pool_48h_volume = BigNumber(0)

    const most_recent_volume_time = volumeResponse.data.at(-1)!.timestamp
    const volume_time_24h_ago = most_recent_volume_time - 24 * 60 * 60
    const volume_time_48h_ago = most_recent_volume_time - 2 * 24 * 60 * 60
    const volume_time_7d_ago = most_recent_volume_time - 7 * 24 * 60 * 60

    // iterate last 30 days
    volumeResponse.data.forEach((daily_volume_data) => {
        const { timestamp, volume_usd } = daily_volume_data
        const is_within_7d = timestamp >= volume_time_7d_ago
        const is_within_24h = timestamp >= volume_time_24h_ago
        const is_within_48h = timestamp >= volume_time_48h_ago

        const day = getDay(timestamp)

        let pool_daily_volume = BigNumber(0)
        const daily_volume_by_token = Object.entries(volume_usd)
        // for each token
        daily_volume_by_token.forEach(([token_symbol, token_volume_obj]) => {
            if (token_symbol == 'WSTETH') {
                token_symbol = 'wstETH'
            }
            const dollar_value = BigNumber(token_volume_obj.usd)

            if (!dollar_value.isZero()) {
                // add to total volume history
                if (!total_volume_by_day[day]) total_volume_by_day[day] = BigNumber(0)
                total_volume_by_day[day] = total_volume_by_day[day].plus(dollar_value)

                if (is_within_7d) {
                    // add day to pool's daily volume,
                    pool_daily_volume = pool_daily_volume.plus(dollar_value)
                }

                if (is_within_24h) {
                    tokens[token_symbol.toUpperCase()].volume_24h = tokens[token_symbol.toUpperCase()].volume_24h.plus(dollar_value)
                    // tokens[token_symbol.toLowerCase()].volume_24h = tokens[token_symbol.toLowerCase()].volume_24h.plus(dollar_value)
                }
            }
        })

        if (is_within_7d) {
            // add this day's volume to the pool's weekly volume
            pool_volume_7d = pool_volume_7d.plus(pool_daily_volume)
        }

        if (is_within_48h) {
            pool_48h_volume = pool_48h_volume.plus(pool_daily_volume)
        }

        if (is_within_24h) {
            // set the pool's 24h volume
            pool_volume_24h = pool_daily_volume
        }
    })

    const pool_volume_prev_24h = pool_48h_volume.minus(pool_volume_24h)
    // const pool_volume_prev_24h = pool_48h_volume.minus(pool_volume_24h)

    return { pool_volume_24h, pool_volume_7d, pool_volume_prev_24h }
}

function processPoolTvlData({
    tvlResponse,
    poolMetadata,
    total_tvl_by_hour,
    token_price_by_hour,
    tokens
}: {
    tvlResponse: TvlResponse
    poolMetadata: PoolMetadata
    total_tvl_by_hour: TotalTvlByHour
    token_price_by_hour: TokenPriceByHour
    tokens: Tokens
}): { pool_tvl: BigNumber; pool_24_tvl: BigNumber } {
    // handle history - for each hour
    tvlResponse.data.forEach((hourly_token_tvl) => {
        const { token_a_tvl_usd, token_b_tvl_usd, timestamp, token_a_tvl, token_b_tvl } = hourly_token_tvl
        const hour = getHour(timestamp)

        if (token_a_tvl_usd || token_b_tvl_usd) {
            // add to toal tvl history
            total_tvl_by_hour[hour] = (total_tvl_by_hour[hour] ?? BigNumber(0)).plus(token_a_tvl_usd ?? 0).plus(token_b_tvl_usd ?? 0)

            // add to tokens price history
            const token_a_price = BigNumber(token_a_tvl_usd ?? 0).div(token_a_tvl)
            const token_b_price = BigNumber(token_b_tvl_usd ?? 0).div(token_b_tvl)

            token_price_by_hour[hour] = token_price_by_hour[hour] ?? {}
            token_price_by_hour[hour][poolMetadata.token0.symbol] = token_a_price
            token_price_by_hour[hour][poolMetadata.token1.symbol] = token_b_price
        }
    })

    const last_tvl = tvlResponse.data.at(-1)!
    // increase token A current tvl
    const token_a = tokens[poolMetadata.token0.symbol]
    token_a.tvl = token_a.tvl.plus(last_tvl.token_a_tvl_usd ?? 0)

    // increase token B current tvl
    const token_b = tokens[poolMetadata.token1.symbol]
    token_b.tvl = token_b.tvl.plus(last_tvl.token_b_tvl_usd ?? 0)

    // get the pool's current tvl
    const pool_tvl = BigNumber(last_tvl.token_a_tvl_usd ?? 0).plus(last_tvl.token_b_tvl_usd ?? 0)
    const pool_24_tvl = BigNumber(tvlResponse.data.at(-50)!.token_a_tvl_usd ?? 0).plus(tvlResponse.data.at(-50)!.token_b_tvl_usd ?? 0)
    return { pool_tvl, pool_24_tvl }
}

/** update the tokens prices and price changes according to the historical data */
const setTokensCurrentPrice = (
    tokens: {
        [token_symbol: string]: ProcessedTokenData
    },
    token_price_by_hour: {
        [floored_hour_timestamp: number]: {
            [token_sym: string]: BigNumber
        }
    }
) => {
    // prep tokens prices
    const [last_timestamp, last_prices] = Object.entries(token_price_by_hour).at(-1)!
    const _24h_ago_timestamp = Number(last_timestamp) - 24 * 60 * 60
    try {
        // const [, _24h_ago_prices] = findLast(Object.entries(token_price_by_hour), ([timestamp]) => Number(timestamp) <= _24h_ago_timestamp || Number(timestamp) >= _24h_ago_timestamp)!
        const [, _24h_ago_prices] = findLast(Object.entries(token_price_by_hour), ([timestamp]) => Number(timestamp) <= _24h_ago_timestamp)! // TODO return this

        // for each token
        Object.entries(tokens).forEach(([symbol, tokenData]) => {
            // get token prices
            const token_current_price = last_prices[symbol] ?? BigNumber(0)

            // calc price chagne
            const token_price_24h_ago = _24h_ago_prices[symbol] ?? BigNumber(0)
            const token_price_change = getChangePercent(token_price_24h_ago, token_current_price)

            tokenData.price = token_current_price
            tokenData.priceChange = token_price_change
        })
    } catch (e) {}
}

const calcTvlSummary = (total_tvl_by_hour: TotalTvlByHour): ProcessedTvlData => {
    const [current_timestamp, current_value] = Object.entries(total_tvl_by_hour).at(-1)!

    const _24h_ago_timestamp = Number(current_timestamp) - 24 * 60 * 60
    // const total
    const history_as_arr = Object.entries(total_tvl_by_hour).map(([hour, tvl]) => ({
        timestamp: Number(hour),
        tvl: Number(tvl.toFixed(2))
    }))
    // const [, _24h_ago_value] = findLast(Object.entries(total_tvl_by_hour), ([timestamp]) => Number(timestamp) <= _24h_ago_timestamp || Number(timestamp) >= _24h_ago_timestamp)!
    try {
        const [, _24h_ago_value] = findLast(Object.entries(total_tvl_by_hour), ([timestamp]) => Number(timestamp) <= _24h_ago_timestamp)! // TODO return this

        const change = getChangePercent(_24h_ago_value, current_value)

        return {
            current: current_value,
            change,
            history: history_as_arr
        }
    } catch (e) {
        return {
            current: current_value,
            change: BigNumber(0),
            history: history_as_arr
        }
    }
}

export function getVolumeDataByWeek(volumeData: TotalVolume[]) {
    const total_volume_by_week: TotalVolumeByWeek = {}
    Object.entries(volumeData).forEach(([timestamp, volume]) => {
        // console.log('timestamp ', timestamp)
        // console.log('volume.timestamp ', volume.timestamp)
        const week = getWeek(Number(volume.timestamp))
        if (!total_volume_by_week[week]) total_volume_by_week[week] = BigNumber(0)
        total_volume_by_week[week] = total_volume_by_week[week].plus(volume.volume)
    })

    const weekly_history_arr = Object.entries(total_volume_by_week).map(([day, volume]) => ({
        timestamp: Number(day),
        volume: Number(volume.toFixed(2))
    }))

    return weekly_history_arr
}

const calcVolumeSummary = (total_volume_by_day: { [floored_day_timestamp: number]: BigNumber }): ProcessedVolumeData => {
    const [current_timestamp, current_value] = Object.entries(total_volume_by_day).at(-1)!
    const _24h_ago_timestamp = Number(current_timestamp) - 24 * 60 * 60
    const [, _24h_ago_value] = findLast(Object.entries(total_volume_by_day), ([timestamp]) => Number(timestamp) <= _24h_ago_timestamp)!

    const change = getChangePercent(_24h_ago_value, current_value)

    const history_as_arr = Object.entries(total_volume_by_day).map(([day, volume]) => ({
        timestamp: Number(day),
        volume: Number(volume.toFixed(2))
    }))

    const total_volume_by_week: TotalVolumeByWeek = {}
    Object.entries(total_volume_by_day).forEach(([timestamp, daily_volume]) => {
        const week = getWeek(Number(timestamp))
        if (!total_volume_by_week[week]) total_volume_by_week[week] = BigNumber(0)
        total_volume_by_week[week] = total_volume_by_week[week].plus(daily_volume)
    })

    const weekly_history_arr = Object.entries(total_volume_by_week).map(([day, volume]) => ({
        timestamp: Number(day),
        volume: Number(volume.toFixed(2))
    }))

    // console.log('useChartsData calcVolumeSummary ', {
    //     current_timestamp: new Date(Number(current_timestamp) * 1000),
    //     current_value: current_value.toString(),
    //     _24h_ago_timestamp: new Date(Number(_24h_ago_timestamp) * 1000),
    //     _24h_ago_value: _24h_ago_value.toString(),
    //     change: change.toString()
    // })

    return {
        current: current_value,
        change,
        history: history_as_arr,
        weekly_history: weekly_history_arr
    }
}

type TokenPriceByHour = {
    [floored_hour_timestamp: number]: {
        [token_sym: string]: BigNumber
    }
}

type TotalTvlByHour = {
    [floored_hour_timestamp: number]: BigNumber
}

type Tokens = {
    [token_symbol: string]: ProcessedTokenData
}

type TotalVolumeByDay = {
    [floored_day_timestamp: number]: BigNumber
}
type TotalVolumeByWeek = {
    [floored_week_timestamp: number]: BigNumber
}

const getHour = (timestamp_sec: number) => {
    const SEC_PER_HOUR = 60 * 60
    return Math.floor(timestamp_sec / SEC_PER_HOUR) * SEC_PER_HOUR
}

export const getDay = (timestamp_sec: number) => {
    const SEC_PER_DAY = 24 * 60 * 60
    return Math.floor(timestamp_sec / SEC_PER_DAY) * SEC_PER_DAY
}

const getWeek = (timestamp_sec: number) => {
    return startOfWeek(timestamp_sec * 1000).getTime() / 1000
}

const getChangePercent = (a: BigNumber, b: BigNumber) => b.div(a).minus(1).multipliedBy(100)

const TOKEN_SYMBOL_TO_NAME: { [symbol: string]: string } = {
    ETH: 'Ether',
    eth: 'Ether',
    USDC: 'USDC Coin',
    usdc: 'USDC Coin',
    DAI: 'Dai Stablecoin',
    USDT: 'Tether USD',
    WBTC: 'Wrapped BTC',
    wbtc: 'Wrapped BTC',
    wstETH: 'Wrapped Staked Ether',
    WSTETH: 'Wrapped Staked Ether',
    LORDS: 'Lords',
    usdt: 'Tether USD',
    dai: 'Dai Stablecoin',
    LUSD: 'LUSD Stablecoin',
    SPEPE: 'StarkPepe',
    rETH: 'Rocket Pool ETH'
}
